/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useMemo } from 'react'
import * as yup from 'yup'

import { t } from '@/i18n'
import { IDriver } from '@/types'
import { useForm } from '@/hooks'
import { Form, FormButtons, TextInput, DatePicker } from '@/components'
import CustomerSelector from '@/pages/Customer/customerSelector'

interface Props {
  isEditing?: boolean
  initialData?: Partial<IDriver>
  loading: boolean
  submitting: boolean
  onCancel: () => void
  onSubmit: (d: IDriver) => void
}

const DriverForm: React.FC<Props> = ({
  isEditing,
  initialData,
  loading,
  submitting,
  onCancel,
  onSubmit,
}) => {
  const schema = useMemo(
    () =>
      yup
        .object({
          name: yup.string().trim().required(t('required_field_error')),
          customer: yup.string().required(t('required_field_error')),
        })
        .required(),
    [],
  )

  const { control, isValid, getValues, onTriggerChange, onFormSubmit } =
    useForm<any>({
      isEditing,
      defaultValues: initialData,
      schema,
      onSubmit,
    })

  useEffect(() => {
    if (initialData?.customer) {
      onTriggerChange('customer', initialData.customer.id)
    }
  }, [initialData?.customer, onTriggerChange])

  const formRef = useRef<HTMLFormElement | null>(null)

  const emptyValue = useMemo(() => (isEditing ? '' : '...'), [isEditing])

  return (
    <div className="edit-info">
      <div className="data-box">
        {loading ? (
          <div className="loading-text">{t('loading')}</div>
        ) : (
          <>
            <Form<IDriver>
              className={isEditing ? 'editing' : 'view'}
              isEditing={isEditing}
              formRef={formRef}
              control={control}
              onSubmit={onFormSubmit}
            >
              <div className="form-fields">
                <TextInput
                  required
                  name="name"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('name')}
                />
                <TextInput
                  name="email"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('email')}
                />
                <TextInput
                  name="address"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('address')}
                />
                <TextInput
                  control={control}
                  className="w-50"
                  name="additionalAddress"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('additional_address')}
                />
                <TextInput
                  name="city"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('city')}
                />
                <TextInput
                  name="zip"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('zip')}
                />
                <TextInput
                  name="phone"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('phone')}
                />
                <TextInput
                  name="country"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('country')}
                  defaultValue={'ישראל'}
                />
                <DatePicker
                  name="birthday"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  dateFormat="yyyy-MM-dd"
                  disabled={!Boolean(isEditing)}
                  label={t('birthday')}
                />
                <TextInput
                  name="idNumber"
                  control={control}
                  className="w-50"
                  emptyValue={emptyValue}
                  disabled={!Boolean(isEditing)}
                  label={t('id_number')}
                />
                <CustomerSelector
                  isFormElement
                  control={control}
                  className="w-50"
                  isEditing={isEditing}
                  initial={initialData?.customer}
                  selected={getValues('customer')}
                />
              </div>
            </Form>

            <FormButtons
              visible={isEditing}
              formRef={formRef}
              isFormValid={isValid}
              submitting={submitting}
              onCancel={onCancel}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default DriverForm
